import { useState } from "react"
import styled, { css } from "styled-components"

import { colors } from "@minuthq/meatball-ui-react"

import { useGetUser } from "src/data/user/hooks/useGetUser"
import { fontWeight } from "src/ui/fontWeight"
import AnonymousIcon from "src/ui/icons/profile-circle.svg"

type AvatarProps = {
  alt?: string
  src?: string
  variant?: "circular" | "rounded"
  className?: string
  children?: React.ReactNode
}

function Avatar({
  alt,
  src,
  variant = "circular",
  className = "",
  children,
}: AvatarProps) {
  const [hasError, setHasError] = useState(false)

  const handleError = () => {
    setHasError(true)
  }

  if (src && !hasError) {
    return (
      <AvatarImage
        src={src}
        alt={alt || ""}
        onError={handleError}
        $variant={variant}
        className={className}
      />
    )
  }

  return (
    <AvatarFallback $variant={variant} className={className}>
      {children || (alt ? alt.split(" ")[0]?.toUpperCase() : null)}
    </AvatarFallback>
  )
}

const baseAvatarStyles = css<{ $variant: "circular" | "rounded" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ $variant }) => ($variant === "circular" ? "50%" : "4px")};
  overflow: hidden;
`

const AvatarImage = styled.img<{ $variant: "circular" | "rounded" }>`
  ${baseAvatarStyles}
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const AvatarFallback = styled.div<{ $variant: "circular" | "rounded" }>`
  ${baseAvatarStyles}
  background-color: ${colors.systemGoodLight};
  color: ${colors.textPrimary};
`

const ProfilePicture = styled(Avatar)`
  width: 32px;
  height: 32px;
`

export function RoundProfilePicture({
  className = "",
  ...props
}: {
  className?: string
  width?: string
  height?: string
}) {
  const user = useGetUser()

  if (user.profile_image_url) {
    return (
      <ProfilePicture
        alt={user.fullname}
        src={user.profile_image_url}
        className={className}
        {...props}
      />
    )
  }

  return (
    <AnonymousIcon fill={colors.primary} width="32" height="32" {...props} />
  )
}

type TAvatarSize = "small" | "medium" | "large"
export function OrganizationAvatar({
  orgName,
  size = "medium",
}: {
  orgName: string | undefined
  size?: TAvatarSize
}) {
  return (
    <StyledOrganizationAvatar variant="circular" size={size}>
      {Array.from(orgName ?? "O")[0]}
    </StyledOrganizationAvatar>
  )
}

const sizeMap: { [K in TAvatarSize]: string } = {
  small: "24px",
  medium: "32px",
  large: "48px",
}
const StyledOrganizationAvatar = styled(Avatar)<{ size: TAvatarSize }>`
  width: ${({ size }) => sizeMap[size]};
  height: ${({ size }) => sizeMap[size]};
  background: ${colors.accentSecondary};
  color: ${colors.textPrimary};
  font-weight: ${fontWeight.semiBold};
  font-size: 17px;
`
